/**
 * The one click that answers a browser refresh: "Continue as {name}?"
 *
 * Deliberately not silent and deliberately not a password. See `useContinueSessionMutation`'s
 * doc comment and `ContinueSessionCommand` on the server for exactly what that trade gives up —
 * this screen exists so the trade is a conscious tap, never something that happens with nobody
 * looking.
 */

import { useState, type SyntheticEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {
  ErrorCode,
  isApiError,
  signedIn,
  switchedUser,
  useContinueSessionMutation,
  type SessionHint,
} from '@pos/shared';

import { useAppDispatch } from '@/app/hooks';

type LocationState = { from?: string } | null;

export function ContinueSessionForm({ hint }: { hint: SessionHint }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [continueSession, { isLoading, error }] = useContinueSessionMutation();
  const [declined, setDeclined] = useState(false);

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      const session = await continueSession().unwrap();

      dispatch(signedIn(session));
      const from = (location.state as LocationState)?.from;
      void navigate(from ?? '/', { replace: true });
    } catch {
      // The hook holds the error; the Alert below renders it. There is nothing to retry into —
      // a failed continue means the device is not eligible any more, not that the tap missed.
    }
  };

  // Declared the moment it happens, not inferred from the error shape: a wrong or expired
  // refresh cookie and "I am not this person" both end the same way, on the password form.
  const spent = declined || (isApiError(error) && error.code !== ErrorCode.Offline);

  if (spent) {
    return (
      <Stack spacing={2}>
        {isApiError(error) && (
          <Alert severity="warning" variant="outlined">
            {error.message}
          </Alert>
        )}
        <Button
          type="button"
          variant="contained"
          size="large"
          fullWidth
          onClick={() => {
            dispatch(switchedUser());
          }}
        >
          Sign in with password
        </Button>
      </Stack>
    );
  }

  return (
    <form onSubmit={(event) => void submit(event)}>
      <Stack spacing={2.5}>
        {error !== undefined && isApiError(error) && (
          <Alert severity="error" variant="outlined">
            {error.message}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={isLoading}
          {...(isLoading ? { startIcon: <CircularProgress size={16} color="inherit" /> } : {})}
        >
          {isLoading ? 'Continuing…' : `Continue as ${firstName(hint.fullName)}`}
        </Button>

        <Typography
          component="button"
          type="button"
          onClick={() => {
            setDeclined(true);
          }}
          variant="body2"
          sx={{
            p: 0,
            border: 0,
            bgcolor: 'transparent',
            color: 'text.secondary',
            textDecoration: 'underline',
            cursor: 'pointer',
            textAlign: 'center',
          }}
        >
          Not you? Sign in as someone else
        </Typography>
      </Stack>
    </form>
  );
}

function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? fullName;
}
